import React, { useCallback, useEffect } from 'react'

import { useForm, useWatch } from 'react-hook-form'

import { Message } from '~/components/component-library'
import { Group, SelectInput } from '~/components/component-library'
import { plural } from '~/utils/display'

export const ThresholdForm = React.memo(
  ({
    defaultRecoveryThreshold,
    signerCount,
    onSelectThreshold,
  }: {
    defaultRecoveryThreshold: number
    signerCount: number
    onSelectThreshold: (threshold: number) => Promise<boolean>
  }) => {
    const { control, setValue } = useForm({
      defaultValues: { recoveryThreshold: defaultRecoveryThreshold },
    })
    const recoveryThreshold = useWatch({ control, name: 'recoveryThreshold' })

    const recommendedThreshold = getRecommendedThreshold(signerCount)

    const setRecoveryThreshold = useCallback(
      async (threshold: number) => {
        const isSaved = await onSelectThreshold(threshold)
        if (isSaved) setValue('recoveryThreshold', threshold)
      },
      [onSelectThreshold, setValue],
    )

    useEffect(() => {
      setValue('recoveryThreshold', defaultRecoveryThreshold)
    }, [defaultRecoveryThreshold, setValue])

    return (
      <Group
        label="Threshold"
        description="How many signers must approve a recovery transaction?"
      >
        <SelectInput
          control={control}
          inputName="recoveryThreshold"
          selectValue={setRecoveryThreshold}
          options={Array.from({ length: signerCount }).map((_, i) => ({
            value: i + 1,
            label: `${i + 1} ${plural('approval', i + 1)} required${
              i + 1 === recommendedThreshold ? ' (recommended)' : ''
            }`,
          }))}
          placeholder="Select threshold"
          selectedOption={recoveryThreshold}
        />
        {recoveryThreshold && recoveryThreshold < recommendedThreshold && (
          <>
            <div className="h-2" />
            <Message variant="warning">
              For security, we recommend a higher approval threshold
            </Message>
          </>
        )}
      </Group>
    )
  },
)

ThresholdForm.displayName = 'ThresholdForm'

export const getRecommendedThreshold = (signerCount: number) => {
  if (signerCount <= 1) return 1
  return Math.floor(signerCount / 2) + 1
}
